import React from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Description,
  CheckCircle,
  Book as BookIcon,
  Link as LinkIcon,
  Person,
  Lightbulb as LightbulbIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

interface NavigationSection {
  title: string;
  icon: React.ReactElement;
  link: string;
  path: string;
  items?: string[];
}

const navigationSections: NavigationSection[] = [
  {
    title: 'КАК ОПУБЛИКОВАТЬ СТАТЬЮ',
    icon: <Description />,
    link: '/authors',
    path: '/authors'
  },
  {
    title: 'ПРАВИЛА ПУБЛИКАЦИИ',
    icon: <BookIcon />,
    link: '/authors/rules',
    path: '/authors/rules',
    // items: [
    //   'Редакционные правила для авторов',
    //   'Правила подготовки рукописей',
    //   'Соблюдение этических норм',
    //   'Оформление информации',
    //   'Электронная корректура'
    // ]
  },
  {
    title: 'ПУБЛИКАЦИОННАЯ ЭТИКА',
    icon: <CheckCircle />,
    link: '/authors/ethics',
    path: '/authors/ethics'
  },
  // Временно скрыты следующие разделы:
  // {
  //   title: 'OPEN ACCESS',
  //   icon: <LinkIcon />,
  //   link: '/authors/open-access',
  //   path: '/authors/open-access',
  //   items: [
  //     'Что такое Open Access',
  //     'Преимущества Open Access',
  //     'Лицензии Open Access',
  //     'Как опубликовать статью'
  //   ]
  // },
  // {
  //   title: 'АВТОРСКИЙ ДОГОВОР',
  //   icon: <Person />,
  //   link: '/authors/agreement',
  //   path: '/authors/agreement'
  // },
  // {
  //   title: 'ИСПОЛЬЗОВАНИЕ ЗАИМСТВОВАННЫХ МАТЕРИАЛОВ',
  //   icon: <LightbulbIcon />,
  //   link: '/authors/borrowed-materials',
  //   path: '/authors/borrowed-materials'
  // },
  // {
  //   title: 'СВЯЗЬ С РЕДАКЦИЕЙ',
  //   icon: <EmailIcon />,
  //   link: '/authors/contact',
  //   path: '/authors/contact'
  // },
  // {
  //   title: 'ВЫПЛАТЫ ГОНОРАРОВ',
  //   icon: <TrendingUpIcon />,
  //   link: '/authors/royalties',
  //   path: '/authors/royalties'
  // },
  // {
  //   title: 'ВОПРОСЫ-ОТВЕТЫ',
  //   icon: <QuestionAnswerIcon />,
  //   link: '/authors/faq',
  //   path: '/authors/faq'
  // }
];

interface AuthorsSidebarProps {
  children: React.ReactNode;
}

const AuthorsSidebar: React.FC<AuthorsSidebarProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  if (isMobile) {
    // На мобильных устройствах показываем только контент без боковой панели
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Основной контент */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>

      {/* Боковая навигация справа */}
      <Box sx={{ width: 300, flexShrink: 0, p: 2 }}>
        <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
          <Typography variant="h6" gutterBottom sx={{ 
            fontWeight: 'bold', 
            textAlign: 'center',
            color: 'primary.main',
            mb: 2
          }}>
            АВТОРАМ
          </Typography>
          
          <List dense>
            {navigationSections.map((section, sectionIndex) => {
              const isActive = isCurrentPath(section.path);
              
              return (
                <Box key={sectionIndex}>
                  <ListItem 
                    component={RouterLink}
                    to={section.link}
                    sx={{ 
                      py: 0.5,
                      px: 1,
                      textDecoration: 'none',
                      borderRadius: 1,
                      mb: 0.5,
                      backgroundColor: isActive ? 'primary.light' : 'transparent',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        backgroundColor: isActive ? 'primary.light' : 'action.hover',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? 'primary.contrastText' : 'inherit' }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={section.title}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.9rem',
                          fontWeight: isActive ? 'bold' : 'medium',
                          color: isActive ? 'primary.contrastText' : 'text.primary'
                        }
                      }}
                    />
                  </ListItem>
                  
                  {section.items && (
                    <List dense sx={{ pl: 4 }}>
                      {section.items.map((item, itemIndex) => (
                        <ListItem 
                          key={itemIndex} 
                          sx={{ 
                            py: 0.3,
                            px: 1,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              borderRadius: 1
                            }
                          }}
                        >
                          <ListItemText 
                            primary={item}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '0.8rem',
                                color: 'text.secondary'
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              );
            })}
          </List>
        </Paper>
      </Box>
    </Box>
  );
};

export default AuthorsSidebar;
